import LBox from "@components/LBox";
import LGradient from "@components/LGradient";
import LIcon from "@components/LIcon";
import { theme } from "@theme";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
} from "react-native";

import AnimatedFilterButton from "./AnimatedFilterButton";
import {
  getFilterOverflowVisibility,
  getOrderedLibraryFilters,
} from "./filterBar";
import { Filter } from "./types";

const LibraryFilterBar = ({
  filter,
  onFilterChange,
}: {
  filter: Filter | null;
  onFilterChange: (filter: Filter | null) => void;
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollMetrics = useRef({
    contentWidth: 0,
    viewportWidth: 0,
    offsetX: 0,
  });
  const [overflow, setOverflow] = useState({
    left: false,
    right: false,
  });

  const updateOverflow = useCallback(
    (metrics: Partial<typeof scrollMetrics.current>) => {
      scrollMetrics.current = {
        ...scrollMetrics.current,
        ...metrics,
      };
      const nextOverflow = getFilterOverflowVisibility(scrollMetrics.current);

      setOverflow((currentOverflow) =>
        currentOverflow.left === nextOverflow.left &&
        currentOverflow.right === nextOverflow.right
          ? currentOverflow
          : nextOverflow,
      );
    },
    [],
  );

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      updateOverflow({ viewportWidth: event.nativeEvent.layout.width });
    },
    [updateOverflow],
  );

  const onContentSizeChange = useCallback(
    (contentWidth: number) => {
      updateOverflow({ contentWidth });
    },
    [updateOverflow],
  );

  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      updateOverflow({ offsetX: event.nativeEvent.contentOffset.x });
    },
    [updateOverflow],
  );

  const orderedFilters = useMemo(
    () => getOrderedLibraryFilters(filter, Platform.OS === "web"),
    [filter],
  );

  useEffect(() => {
    scrollViewRef.current?.scrollTo({ x: 0, animated: true });
    updateOverflow({ offsetX: 0 });
  }, [filter, updateOverflow]);

  return (
    <LBox style={{ marginHorizontal: -theme.spacing.s4 }}>
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
        onScroll={onScroll}
        scrollEventThrottle={16}
      >
        <LBox flexDirection="row" gap="s2" alignItems="center" px="s4">
          {orderedFilters.map((config) => (
            <AnimatedFilterButton
              key={config.value}
              config={config}
              filter={filter}
              onPress={() =>
                onFilterChange(filter === config.value ? null : config.value)
              }
            />
          ))}
        </LBox>
      </ScrollView>
      {overflow.left ? (
        <LGradient
          colors={[theme.colors.screenBg, theme.colors.transparent]}
          gradientDirection="horizontal"
          pointerEvents="none"
          accessible={false}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          testID="library-filter-overflow-left"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            top: 0,
            width: 44,
          }}
        >
          <LBox
            flex={1}
            alignItems="flex-start"
            justifyContent="center"
            pl="s2"
          >
            <LIcon name="chevron" rotate={90} size={12} stroke="white" />
          </LBox>
        </LGradient>
      ) : null}
      {overflow.right ? (
        <LGradient
          colors={[theme.colors.transparent, theme.colors.screenBg]}
          gradientDirection="horizontal"
          pointerEvents="none"
          accessible={false}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          testID="library-filter-overflow-right"
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            top: 0,
            width: 44,
          }}
        >
          <LBox flex={1} alignItems="flex-end" justifyContent="center" pr="s2">
            <LIcon name="chevron" rotate={270} size={12} stroke="white" />
          </LBox>
        </LGradient>
      ) : null}
    </LBox>
  );
};

export default LibraryFilterBar;
